// Block types offered by the "/" menu and the "Turn into" menu.
// `run` receives a focused chain (with the "/query" text already removed when
// used from the slash menu) and must call `.run()`. Media items also get
// `openMedia(type)` to show the upload / embed picker, and the AI item
// `openAi()` to show the inline AI prompt.
const openMediaPicker = type => (chain, { openMedia }) => {
  chain.run()
  openMedia(type)
}

const blockCommands = [
  {
    id: 'ai',
    group: 'AI',
    title: 'Ask AI',
    description: 'Write or draft with AI.',
    icon: '✦',
    keywords: ['ai', 'write', 'draft', 'generate', 'assistant'],
    run: (chain, { openAi }) => {
      chain.run()
      openAi()
    }
  },
  {
    id: 'paragraph',
    title: 'Text',
    description: 'Just start writing with plain text.',
    icon: 'T',
    keywords: ['paragraph', 'text', 'plain'],
    turnInto: true,
    isActive: editor => editor.isActive('paragraph') && !editor.isActive('bulletList') &&
      !editor.isActive('orderedList') && !editor.isActive('taskList') && !editor.isActive('blockquote'),
    run: chain => chain.clearNodes().setParagraph().run()
  },
  {
    id: 'heading1',
    title: 'Heading 1',
    description: 'Big section heading.',
    icon: 'H1',
    keywords: ['h1', 'title', 'heading'],
    turnInto: true,
    isActive: editor => editor.isActive('heading', { level: 1 }),
    run: chain => chain.clearNodes().setHeading({ level: 1 }).run()
  },
  {
    id: 'heading2',
    title: 'Heading 2',
    description: 'Medium section heading.',
    icon: 'H2',
    keywords: ['h2', 'subtitle', 'heading'],
    turnInto: true,
    isActive: editor => editor.isActive('heading', { level: 2 }),
    run: chain => chain.clearNodes().setHeading({ level: 2 }).run()
  },
  {
    id: 'heading3',
    title: 'Heading 3',
    description: 'Small section heading.',
    icon: 'H3',
    keywords: ['h3', 'heading'],
    turnInto: true,
    isActive: editor => editor.isActive('heading', { level: 3 }),
    run: chain => chain.clearNodes().setHeading({ level: 3 }).run()
  },
  {
    id: 'bulletList',
    title: 'Bulleted list',
    description: 'Create a simple bulleted list.',
    icon: '•',
    keywords: ['ul', 'unordered', 'bullet', 'list'],
    turnInto: true,
    isActive: editor => editor.isActive('bulletList'),
    run: chain => chain.clearNodes().toggleBulletList().run()
  },
  {
    id: 'orderedList',
    title: 'Numbered list',
    description: 'Create a list with numbering.',
    icon: '1.',
    keywords: ['ol', 'ordered', 'numbered', 'list'],
    turnInto: true,
    isActive: editor => editor.isActive('orderedList'),
    run: chain => chain.clearNodes().toggleOrderedList().run()
  },
  {
    id: 'taskList',
    title: 'To-do list',
    description: 'Track tasks with a to-do list.',
    icon: '☐',
    keywords: ['todo', 'task', 'checkbox', 'check'],
    turnInto: true,
    isActive: editor => editor.isActive('taskList'),
    run: chain => chain.clearNodes().toggleTaskList().run()
  },
  {
    id: 'blockquote',
    title: 'Quote',
    description: 'Capture a quote.',
    icon: '❝',
    keywords: ['quote', 'blockquote', 'citation'],
    turnInto: true,
    isActive: editor => editor.isActive('blockquote'),
    run: chain => chain.clearNodes().toggleBlockquote().run()
  },
  {
    id: 'codeBlock',
    title: 'Code',
    description: 'Capture a code snippet.',
    icon: '</>',
    keywords: ['code', 'snippet', 'pre'],
    turnInto: true,
    isActive: editor => editor.isActive('codeBlock'),
    run: chain => chain.clearNodes().toggleCodeBlock().run()
  },
  {
    id: 'table',
    title: 'Table',
    description: 'Add a simple table.',
    icon: '⊞',
    keywords: ['table', 'grid', 'rows', 'columns'],
    run: chain => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  },
  {
    id: 'divider',
    title: 'Divider',
    description: 'Visually divide blocks.',
    icon: '—',
    keywords: ['divider', 'hr', 'line', 'separator'],
    run: chain => chain.setHorizontalRule().run()
  },
  {
    id: 'image',
    group: 'Media',
    title: 'Image',
    description: 'Upload or embed with a link.',
    icon: '🖼',
    keywords: ['image', 'picture', 'photo', 'img'],
    run: openMediaPicker('image')
  },
  {
    id: 'video',
    group: 'Media',
    title: 'Video',
    description: 'Upload or embed from YouTube or a link.',
    icon: '▶',
    keywords: ['video', 'movie', 'youtube', 'embed'],
    run: openMediaPicker('video')
  },
  {
    id: 'audio',
    group: 'Media',
    title: 'Audio',
    description: 'Upload or embed with a link.',
    icon: '♫',
    keywords: ['audio', 'sound', 'music', 'mp3'],
    run: openMediaPicker('audio')
  },
  {
    id: 'file',
    group: 'Media',
    title: 'File',
    description: 'Upload or link to a file.',
    icon: '📎',
    keywords: ['file', 'attachment', 'upload', 'pdf', 'document'],
    run: openMediaPicker('file')
  },
  {
    id: 'bookmark',
    group: 'Media',
    title: 'Web bookmark',
    description: 'Save a link as a visual bookmark.',
    icon: '🔖',
    keywords: ['bookmark', 'link', 'url', 'web'],
    run: openMediaPicker('bookmark')
  }
].map(item => ({ group: 'Basic blocks', ...item }))

export const filterBlockCommands = (query) => {
  const q = (query || '').trim().toLowerCase()
  if (!q) {
    return blockCommands
  }
  return blockCommands.filter(item =>
    item.title.toLowerCase().includes(q) || item.keywords.some(keyword => keyword.startsWith(q))
  )
}

export const turnIntoCommands = blockCommands.filter(item => item.turnInto)

export default blockCommands
